export type TourStatus = 'upcoming' | 'ongoing' | 'past'

/**
 * Calculates the status of a tour based on its departure and return dates
 * @param departureDate - The departure date (YYYY-MM-DD format)
 * @param returnDate - The return date (YYYY-MM-DD format)
 * @returns The calculated status
 */
export function calculateTourStatus(departureDate: string, returnDate: string): TourStatus {
  const today = new Date()
  const departure = new Date(departureDate)
  const returnDateObj = new Date(returnDate)
  
  // Set time to start of day for accurate comparison
  today.setHours(0, 0, 0, 0)
  departure.setHours(0, 0, 0, 0)
  returnDateObj.setHours(0, 0, 0, 0)
  
  // If today is before departure date
  if (today < departure) {
    return 'upcoming'
  }
  
  // If today is between departure and return dates (inclusive)
  if (today >= departure && today <= returnDateObj) {
    return 'ongoing'
  }
  
  // If today is after return date
  return 'past'
}

/**
 * Gets a human-readable status label
 * @param status - The tour status
 * @returns A formatted status label
 */
export function getStatusLabel(status: TourStatus): string {
  switch (status) {
    case 'upcoming':
      return 'Upcoming'
    case 'ongoing':
      return 'Ongoing'
    case 'past':
      return 'Past'
    default:
      return 'Unknown'
  }
}

/**
 * Gets status-specific styling classes
 * @param status - The tour status
 * @returns CSS classes for styling
 */
export function getStatusClasses(status: TourStatus): string {
  switch (status) {
    case 'upcoming':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    case 'ongoing':
      return 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary'
    case 'past':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  }
}

/**
 * Gets status-specific icon
 * @param status - The tour status
 * @returns Icon name or emoji
 */
export function getStatusIcon(status: TourStatus): string {
  switch (status) {
    case 'upcoming':
      return '📅'
    case 'ongoing':
      return '🚀'
    case 'past':
      return '✅'
    default:
      return '❓'
  }
}
